/**
 * DataSource: TARP v5.10 (GUPM-169).
 * ----------------------------------
 * Choke point de persistência de `tarp_records`. A UI nunca chama
 * `supabase.from('tarp_records')` — sempre via este adapter (regra #2).
 *
 * Hoje (POC): Supabase direto. Slot para integração futura (ex.: PAEBM
 * digital Galvani/Walm) troca só a implementação, não a interface.
 *
 * Auditoria: o trigger genérico `audit_tarp_records` grava `audit_log` em
 * toda mutação (mesma transação — antes de retornar). Broadcast `tarp:critical`
 * (regra #3): as transições de negócio (`openTarp`/`confirmAbertura` = abertura,
 * `conclude`/`assume`/`close`) emitem eventos notificáveis; o rascunho emite
 * `refresh` — só sincroniza a lista dos outros usuários ao vivo, sem notificar
 * (GUPM-178) — em `openDraft`/`discardDraft`. O autosave (`saveDraft`) é
 * silencioso: a lista não mostra `flow_state`, então propagar a cada tecla só
 * geraria storm de refetch.
 */

import { supabase } from '@/lib/supabase';
import type { AutoriaEntrada, MomentoAutoria } from '@/domain/tarp/autoria';
import type { FlowState } from '@/domain/tarp/flow';
import type { TarpRecordStatus } from '@/domain/tarp/status';
import type { InstrumentType } from '@/domain';
import { INSTRUMENT_TYPE_ORDER } from '@/domain/instrumentos/instrument-type';
import {
  buildTarpCriticalPayload,
  emitTarpCritical,
  type TarpCriticalEvent,
} from './tarp-realtime';
import type { DataSourceMeta } from './types';

export interface TarpRecord {
  id: string;
  seq: number;
  /** null quando a TARP foi aberta sem barragem identificada (GUPM-186). */
  damId: string | null;
  /** '' quando não há barragem (a UI mostra "Ainda não identificada"). */
  damCode: string;
  instrumentId: string | null;
  instrumentCode: string | null;
  /** C-88 — tipo do instrumento (título e rótulo da TARP); null sem instrumento. */
  instrumentType?: InstrumentType | null;
  tarpCode: string;
  status: TarpRecordStatus;
  stepsTotal: number | null;
  stepsCompleted: number;
  openedAt: string;
  openedById: string | null;
  /** Nome de quem abriu, resolvido via app_users_public (null = não resolvido). */
  openedByName: string | null;
  concludedAt: string | null;
  closedAt: string | null;
  closureComment: string | null;
  anmEnquadramento: 'NE-1' | 'NE-2' | 'NE-3' | null;
  planoAcao: string | null;
  baixaTotpVerified: boolean;
  /** GUPM-178: progresso do checklist salvo no rascunho (retomar de onde parou). */
  flowState: FlowState | null;
  /** GUPM-178: observação em texto livre digitada durante o preenchimento. */
  observation: string | null;
  /** GUPM-178: soft-delete de rascunho descartado (nulo = ativo). */
  deletedAt: string | null;
}

export interface OpenTarpInput {
  /** null = "Ainda não identificada" (GUPM-186); a coluna dam_id aceita null. */
  damId: string | null;
  instrumentId?: string | null;
  tarpCode: string;
  stepsTotal?: number | null;
}

/** GUPM-178: conteúdo autosalvo de um rascunho "Em abertura". */
export interface DraftPatch {
  flowState?: FlowState;
  observation?: string;
  stepsCompleted?: number;
}

/** Baixa técnica do RT (GUPM-176): parecer + enquadramento ANM + plano + flag TOTP. */
export interface CloseTarpInput {
  parecer: string;
  enquadramento: 'NE-1' | 'NE-2' | 'NE-3' | null;
  planoAcao: string;
  totpVerified: boolean;
}

export interface ListTarpsFilter {
  status?: TarpRecordStatus[];
  damId?: string;
}

/**
 * Uma atribuição de ato a uma pessoa (GUPM-292). Derivada do `audit_log` pela
 * função `tarp_autoria`; **nunca** carrega payload — só quem, quando e o quê.
 *
 * O SHAPE vive no domínio (`domain/tarp/autoria`), que é quem sabe o que é um
 * momento de autoria; aqui só se dá o nome pelo qual a UI o importa. Uma
 * definição paralela viraria duas verdades sobre a mesma coisa.
 */
export type AutoriaTarp = AutoriaEntrada;

/** Linha crua da RPC `tarp_autoria` (snake_case do Postgres). */
interface AutoriaRow {
  tarp_id: string;
  momento: MomentoAutoria;
  actor_name: string | null;
  ts: string;
}

export interface TarpDataSource extends DataSourceMeta {
  listTarps(filter?: ListTarpsFilter): Promise<TarpRecord[]>;
  /**
   * GUPM-292: autoria de um CONJUNTO de TARPs (quem iniciou/editou/abriu/deu
   * baixa). Em lote de propósito: a lista do M3 resolve todos os cards numa
   * chamada, em vez de N+1. Array vazio → sem ida ao servidor.
   */
  listAutoria(tarpIds: string[]): Promise<AutoriaTarp[]>;
  /**
   * GUPM-182: baixas ENCERRADAS ordenadas por `closed_at` desc — a baixa mais
   * recente de cada barragem (a declaração ANM vigente) fica no topo, então o
   * cap de 200 nunca descarta uma declaração ativa. Difere de `listTarps`, que
   * ordena por `opened_at` (uma TARP aberta há muito mas baixada agora cairia
   * fora da janela → a barragem perderia a promoção sem erro). Alimenta
   * `declaracaoAnmAtivaPorBarragem`.
   */
  listBaixasEncerradas(): Promise<TarpRecord[]>;
  getTarp(id: string): Promise<TarpRecord | null>;
  openTarp(input: OpenTarpInput): Promise<TarpRecord>;
  /**
   * GUPM-178: cria rascunho persistente (status EM_ABERTURA). Emite `refresh`
   * (sincroniza a lista dos outros usuários — NÃO é notificação). Autosave do
   * checklist via `saveDraft`; vira "aberta de verdade" via `confirmAbertura`.
   */
  openDraft(input: OpenTarpInput, initial?: DraftPatch): Promise<TarpRecord>;
  /** GUPM-178: autosave do rascunho (flow_state/observação/passos). Silencioso (evita storm). */
  saveDraft(id: string, patch: DraftPatch): Promise<TarpRecord>;
  /** GUPM-178: confirma a abertura (EM_ABERTURA → ABERTA). Emite `open` (evento real). */
  confirmAbertura(id: string): Promise<TarpRecord>;
  /** GUPM-178: descarta rascunho (soft-delete). RLS/trigger: autor/RT/admin, só EM_ABERTURA. Emite `refresh`. */
  discardDraft(id: string): Promise<void>;
  updateProgress(id: string, stepsCompleted: number): Promise<TarpRecord>;
  /** Operador conclui e encaminha ao RT (handoff): status → CONCLUIDA_AGUARDANDO. */
  concludeTarp(id: string): Promise<TarpRecord>;
  /** RT assume a análise: status → EM_ANALISE_RT (GUPM-175). */
  assumeTarp(id: string): Promise<TarpRecord>;
  /** RT dá baixa (encerra): status → ENCERRADA. RLS bloqueia operador. */
  closeTarp(id: string, input: CloseTarpInput): Promise<TarpRecord>;
}

const SELECT =
  'id, seq, dam_id, instrument_id, tarp_code, status, steps_total, steps_completed, opened_at, opened_by, concluded_at, closed_at, closure_comment, anm_enquadramento, plano_acao, baixa_totp_verified, flow_state, observation, deleted_at, ' +
  'dam:dams!tarp_records_dam_id_fkey(code), ' +
  'instrument:instruments!tarp_records_instrument_id_fkey(code, type), ' +
  // Autor resolvido pela VIEW pública (mesmo padrão do shift-handover) — expõe só `name`,
  // nunca a tabela app_users direta (totp_secret protegido). RLS da view segue valendo.
  'openedBy:app_users_public!tarp_records_opened_by_fkey(name)';

type Embedded = { code: string } | { code: string }[] | null;
type EmbeddedInstrument = { code: string; type?: string | null } | { code: string; type?: string | null }[] | null;
type EmbeddedName = { name: string } | { name: string }[] | null;

interface TarpRow {
  id: string;
  seq: number;
  dam_id: string | null;
  instrument_id: string | null;
  tarp_code: string | null;
  status: TarpRecordStatus;
  steps_total: number | null;
  steps_completed: number;
  opened_at: string;
  opened_by: string | null;
  concluded_at: string | null;
  closed_at: string | null;
  closure_comment: string | null;
  anm_enquadramento: 'NE-1' | 'NE-2' | 'NE-3' | null;
  plano_acao: string | null;
  baixa_totp_verified: boolean;
  flow_state: FlowState | null;
  observation: string | null;
  deleted_at: string | null;
  dam?: Embedded;
  instrument?: EmbeddedInstrument;
  openedBy?: EmbeddedName;
}

function unwrapCode(raw: Embedded | undefined): string | null {
  const one = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
  return one ? one.code : null;
}

function unwrapType(raw: EmbeddedInstrument | undefined): InstrumentType | null {
  const one = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
  const t = one?.type;
  return t && (INSTRUMENT_TYPE_ORDER as readonly string[]).includes(t) ? (t as InstrumentType) : null;
}

function unwrapName(raw: EmbeddedName | undefined): string | null {
  const one = Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null);
  return one ? one.name : null;
}

function mapRow(r: TarpRow): TarpRecord {
  return {
    id: r.id,
    seq: r.seq,
    damId: r.dam_id,
    damCode: unwrapCode(r.dam) ?? '',
    instrumentId: r.instrument_id,
    instrumentCode: unwrapCode(r.instrument),
    instrumentType: unwrapType(r.instrument),
    tarpCode: r.tarp_code ?? '',
    status: r.status,
    stepsTotal: r.steps_total,
    stepsCompleted: r.steps_completed,
    openedAt: r.opened_at,
    openedById: r.opened_by,
    openedByName: unwrapName(r.openedBy),
    concludedAt: r.concluded_at,
    closedAt: r.closed_at,
    closureComment: r.closure_comment,
    anmEnquadramento: r.anm_enquadramento,
    planoAcao: r.plano_acao,
    baixaTotpVerified: r.baixa_totp_verified,
    flowState: r.flow_state,
    observation: r.observation,
    deletedAt: r.deleted_at,
  };
}

/** Resolve o registro `app_users` do usuário autenticado (opened_by/closed_by). */
async function currentAppUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const filter = user.email
    ? `auth_user_id.eq.${user.id},email.eq.${user.email}`
    : `auth_user_id.eq.${user.id}`;
  const { data, error } = await supabase
    .from('app_users_public')
    .select('id')
    .or(filter)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.id ?? null;
}

function notify(event: TarpCriticalEvent, rec: TarpRecord): void {
  emitTarpCritical(
    buildTarpCriticalPayload(event, {
      id: rec.id,
      damId: rec.damId,
      instrumentId: rec.instrumentId,
      status: rec.status,
    }),
  );
}

export const manualTarp: TarpDataSource = {
  mode: 'manual',

  async listTarps(filter) {
    let q = supabase
      .from('tarp_records')
      .select(SELECT)
      .is('deleted_at', null) // GUPM-178: rascunhos descartados somem das listas
      .order('opened_at', { ascending: false })
      .limit(200);
    if (filter?.damId) q = q.eq('dam_id', filter.damId);
    if (filter?.status && filter.status.length > 0) q = q.in('status', filter.status);
    const { data, error } = await q.returns<TarpRow[]>();
    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async listBaixasEncerradas() {
    // Ordena por closed_at desc (não opened_at, como listTarps): a baixa vigente
    // de cada barragem fica no topo → o limit de 200 nunca corta uma declaração
    // ANM ativa. NULLS por último (uma ENCERRADA real sempre grava closed_at).
    // GUPM-182 (finding do review): alinha a query à regra de vigência do domínio.
    const { data, error } = await supabase
      .from('tarp_records')
      .select(SELECT)
      .eq('status', 'ENCERRADA')
      .is('deleted_at', null)
      .order('closed_at', { ascending: false, nullsFirst: false })
      .limit(200)
      .returns<TarpRow[]>();
    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getTarp(id) {
    const { data, error } = await supabase
      .from('tarp_records')
      .select(SELECT)
      .eq('id', id)
      .is('deleted_at', null) // GUPM-178: rascunho descartado não é legível pela UI
      .maybeSingle()
      .returns<TarpRow | null>();
    if (error) throw error;
    return data ? mapRow(data) : null;
  },

  async openTarp(input) {
    const openedBy = await currentAppUserId();
    const { data, error } = await supabase
      .from('tarp_records')
      .insert({
        dam_id: input.damId,
        instrument_id: input.instrumentId ?? null,
        tarp_code: input.tarpCode,
        status: 'ABERTA',
        steps_total: input.stepsTotal ?? null,
        opened_by: openedBy,
      })
      .select(SELECT)
      .single()
      .returns<TarpRow>();
    if (error) throw error;
    if (!data) throw new Error('insert de tarp_record não retornou row');
    const rec = mapRow(data);
    notify('open', rec);
    return rec;
  },

  async openDraft(input, initial) {
    const openedBy = await currentAppUserId();
    const { data, error } = await supabase
      .from('tarp_records')
      .insert({
        dam_id: input.damId,
        instrument_id: input.instrumentId ?? null,
        tarp_code: input.tarpCode,
        status: 'EM_ABERTURA',
        steps_total: input.stepsTotal ?? null,
        steps_completed: initial?.stepsCompleted ?? 0,
        flow_state: initial?.flowState ?? null,
        observation: initial?.observation ?? null,
        opened_by: openedBy,
      })
      .select(SELECT)
      .single()
      .returns<TarpRow>();
    if (error) throw error;
    if (!data) throw new Error('openDraft não retornou row');
    const rec = mapRow(data);
    // 'refresh' (não notificação): sincroniza a lista de quem está no M3 — outro
    // usuário vê o rascunho surgir e pode assumir. Rascunho nunca ALERTA ninguém.
    notify('refresh', rec);
    return rec;
  },

  async saveDraft(id, patch) {
    const update: Record<string, unknown> = {};
    if (patch.flowState !== undefined) update.flow_state = patch.flowState;
    if (patch.observation !== undefined) update.observation = patch.observation;
    if (patch.stepsCompleted !== undefined) update.steps_completed = patch.stepsCompleted;
    // Nada a salvar → devolve o estado atual sem um PATCH vazio (PostgREST rejeita).
    if (Object.keys(update).length === 0) {
      const rec = await manualTarp.getTarp(id);
      if (!rec) throw new Error('saveDraft: rascunho inexistente');
      return rec;
    }
    const { data, error } = await supabase
      .from('tarp_records')
      .update(update)
      .eq('id', id)
      .select(SELECT)
      .single()
      .returns<TarpRow>();
    if (error) throw error;
    if (!data) throw new Error('saveDraft não retornou row');
    // Autosave é silencioso — SEM broadcast.
    return mapRow(data);
  },

  async confirmAbertura(id) {
    // Idempotente: só transiciona (e notifica) um rascunho EM_ABERTURA. Se já
    // está ABERTA (ex.: retry após o conclude falhar), não re-emite broadcast.
    const { data, error } = await supabase
      .from('tarp_records')
      .update({ status: 'ABERTA' })
      .eq('id', id)
      .eq('status', 'EM_ABERTURA')
      .select(SELECT)
      .maybeSingle()
      .returns<TarpRow | null>();
    if (error) throw error;
    if (data) {
      const rec = mapRow(data);
      notify('open', rec); // a abertura de verdade é o único evento notificável
      return rec;
    }
    const current = await manualTarp.getTarp(id);
    if (!current) throw new Error('confirmAbertura: TARP inexistente');
    return current;
  },

  async listAutoria(tarpIds) {
    // Sem ids não há o que perguntar — evita RPC com array vazio a cada render
    // da lista vazia.
    if (tarpIds.length === 0) return [];
    const { data, error } = await supabase.rpc('tarp_autoria', { p_tarp_ids: tarpIds });
    if (error) throw error;
    return ((data ?? []) as AutoriaRow[]).map((r): AutoriaTarp => ({
      tarpId: r.tarp_id,
      momento: r.momento,
      actorName: r.actor_name,
      ts: r.ts,
    }));
  },

  async discardDraft(id) {
    // Lê antes (rascunho ainda ativo, deleted_at null) pra ter dam/instrumento
    // no payload do refresh — evita depender de SELECT pós-soft-delete.
    const before = await manualTarp.getTarp(id);
    // Soft-delete. RLS + trigger garantem: autor OU RT/admin, só em EM_ABERTURA.
    const { error } = await supabase
      .from('tarp_records')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
    // 'refresh' (não notificação): o rascunho some da lista de todos ao vivo.
    if (before) notify('refresh', before);
  },

  async updateProgress(id, stepsCompleted) {
    const { data, error } = await supabase
      .from('tarp_records')
      .update({ steps_completed: stepsCompleted })
      .eq('id', id)
      .select(SELECT)
      .single()
      .returns<TarpRow>();
    if (error) throw error;
    if (!data) throw new Error('update de progresso não retornou row');
    const rec = mapRow(data);
    notify('update', rec);
    return rec;
  },

  async concludeTarp(id) {
    const concludedBy = await currentAppUserId();
    const { data, error } = await supabase
      .from('tarp_records')
      .update({
        status: 'CONCLUIDA_AGUARDANDO',
        concluded_at: new Date().toISOString(),
        concluded_by: concludedBy,
      })
      .eq('id', id)
      .select(SELECT)
      .single()
      .returns<TarpRow>();
    if (error) throw error;
    if (!data) throw new Error('conclude não retornou row');
    const rec = mapRow(data);
    notify('update', rec);
    return rec;
  },

  async assumeTarp(id) {
    const { data, error } = await supabase
      .from('tarp_records')
      .update({ status: 'EM_ANALISE_RT' })
      .eq('id', id)
      .select(SELECT)
      .single()
      .returns<TarpRow>();
    if (error) throw error;
    if (!data) throw new Error('assume não retornou row');
    const rec = mapRow(data);
    notify('update', rec);
    return rec;
  },

  async closeTarp(id, input) {
    const closedBy = await currentAppUserId();
    const { data, error } = await supabase
      .from('tarp_records')
      .update({
        status: 'ENCERRADA',
        closure_comment: input.parecer,
        anm_enquadramento: input.enquadramento,
        plano_acao: input.planoAcao.trim().length > 0 ? input.planoAcao.trim() : null,
        baixa_totp_verified: input.totpVerified,
        closed_by: closedBy,
        closed_at: new Date().toISOString(),
      })
      .eq('id', id)
      // Guarda de transição (F1): só encerra uma TARP pós-handoff — pular direto de
      // ABERTA/EM_ABERTURA burlaria o protocolo do operador. Espelha o padrão de
      // `confirmAbertura` (.eq('status','EM_ABERTURA')). O trigger tarp_records_write_guard
      // é o gate autoritativo server-side; aqui é defesa-em-profundidade + erro claro.
      .in('status', ['CONCLUIDA_AGUARDANDO', 'EM_ANALISE_RT'])
      .select(SELECT)
      .maybeSingle()
      .returns<TarpRow | null>();
    if (error) throw error;
    if (!data) {
      throw new Error(
        'Baixa indisponível: a TARP precisa estar concluída e encaminhada (aguardando RT) antes do encerramento.',
      );
    }
    const rec = mapRow(data);
    notify('close', rec);
    return rec;
  },
};
